import { mapTransferStatus } from '../components/common/status';
import {
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_TRANSIT_LABEL,
} from '../constants/inventory-status-labels';
import {
  sumTransferItemsCost,
  sumTransferItemsSale,
  type TransferDetail,
} from '../components/inventory/InventoryTransfer/InventoryTransfer.hooks';
import { formatCurrency } from './formatters/money';
import {
  buildPrintDocFooter,
  buildPrintDocHeader,
  buildPrintDocInfoField,
  escapeHtml,
  openPrintDocument,
  statusBadgeClassFromLabel,
  wrapPrintDocument,
} from './printDocument';

function itemStatusClass(status: string): string {
  if (status === INV_STATUS_BRANCH_LABEL) return 'status-assigned';
  if (status === INV_STATUS_TRANSIT_LABEL || status === 'En Tránsito') return 'status-in-transit';
  return 'status-unassigned';
}

/**
 * Print handler for inventory transfer details (LlamaOne document personality).
 * Pass `targetWindow` from openBlankPrintWindow when printing after an async fetch.
 */
export const printInventoryTransfer = (
  transfer: TransferDetail,
  targetWindow?: Window | null,
  options?: {
    showTotalCostAmount?: boolean;
    showTotalSaleAmount?: boolean;
  },
): void => {
  if (!transfer) return;

  const showTotalCostAmount = options?.showTotalCostAmount ?? false;
  const showTotalSaleAmount = options?.showTotalSaleAmount ?? false;

  const statusChip = mapTransferStatus(transfer.status);
  const statusLabel = statusChip.label;
  const items = transfer.items ?? [];

  const itemsTableHtml =
    items.length > 0
      ? `
    <table>
      <thead>
        <tr>
          <th>Código</th>
          <th>Producto</th>
          <th>Tipo</th>
          <th>Ubicación</th>
          <th>Estado</th>
        </tr>
      </thead>
      <tbody>
        ${items
          .map(
            (item) => `
          <tr>
            <td>${escapeHtml(item.productCode) || '-'}</td>
            <td>${escapeHtml(item.productName) || '-'}</td>
            <td>${escapeHtml(item.productType) || '-'}</td>
            <td>${escapeHtml(item.location) || '-'}</td>
            <td>
              <span class="${itemStatusClass(item.status)}">
                ${escapeHtml(item.status)}
              </span>
            </td>
          </tr>
        `,
          )
          .join('')}
      </tbody>
    </table>
  `
      : '<div class="product-item">No hay piezas en esta transferencia</div>';

  const bodyHtml = `
    ${buildPrintDocHeader('Detalles de Transferencia', transfer.transferCode)}

    <div class="info-card">
      <div class="info-grid">
        ${buildPrintDocInfoField('Código de transferencia', escapeHtml(transfer.transferCode))}
        ${buildPrintDocInfoField(
          'Estado',
          `<span class="status-badge ${statusBadgeClassFromLabel(statusLabel)}">${escapeHtml(statusLabel)}</span>`,
        )}
        ${buildPrintDocInfoField('Ubicación origen', escapeHtml(transfer.source) || 'N/A')}
        ${buildPrintDocInfoField('Ubicación destino', escapeHtml(transfer.destination) || 'N/A')}
        ${buildPrintDocInfoField('Creado por', escapeHtml(transfer.createdBy) || 'N/A')}
        ${buildPrintDocInfoField('Fecha', escapeHtml(transfer.date) || 'N/A')}
        ${
          transfer.completedBy
            ? buildPrintDocInfoField('Aceptado por', escapeHtml(transfer.completedBy))
            : ''
        }
        ${
          transfer.cancelledBy
            ? buildPrintDocInfoField('Cancelado por', escapeHtml(transfer.cancelledBy))
            : ''
        }
        ${
          transfer.notes
            ? buildPrintDocInfoField(
                'Notas / observaciones',
                `<div class="info-notes">${escapeHtml(transfer.notes)}</div>`,
                true,
              )
            : ''
        }
      </div>
    </div>

    <div class="section">
      <div class="section-header">Productos en transferencia (${items.length})</div>
      <div style="padding: 8px 12px 12px;">
        ${itemsTableHtml}
      </div>
      ${
        items.length > 0
          ? `
        <div class="total-section">
          <div class="total-item">
            <span class="total-label">Total de piezas</span>
            <span class="total-value">${items.length} piezas</span>
          </div>
          ${
            showTotalCostAmount
              ? `
          <div class="total-item">
            <span class="total-label">Costo total</span>
            <span class="total-value total-value-cost">${escapeHtml(formatCurrency(sumTransferItemsCost(items)))}</span>
          </div>
          `
              : ''
          }
          ${
            showTotalSaleAmount
              ? `
          <div class="total-item">
            <span class="total-label">Precio de venta total</span>
            <span class="total-value total-value-sale">${escapeHtml(formatCurrency(sumTransferItemsSale(items)))}</span>
          </div>
          `
              : ''
          }
        </div>
      `
          : ''
      }
    </div>

    ${buildPrintDocFooter()}
  `;

  openPrintDocument(
    wrapPrintDocument({
      documentTitle: `Detalles de Transferencia - ${transfer.transferCode}`,
      bodyHtml,
    }),
    'printInventoryTransfer',
    targetWindow != null ? { targetWindow } : undefined,
  );
};
